import { IKType } from "@kuika/kuika-cl-model";
import { useMemo } from "react";
import KuikaSmallSpinner from "../../../ui-kit/kuika-small-spinner/kuika-small-spinner";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { IDataTypePanelCategory, IDataTypePanelOption, KuikaDataTypePanel } from "./kuika-data-type-panel";

/**
 * The in-cell data-type picker's panel with its two tabs: a "Primitive Type" list and the
 * project's Kuika Data Types catalog. It is `KuikaDataTypePanel`'s two-tab preset — the search,
 * the tabs and the list are the panel's — and like it renders ONLY the panel, so any combobox
 * can host it inside its own popover.
 *
 * The DEFAULT primitive list is the WORKFLOW parameter vocabulary, which speaks
 * `DateTimeOffset` rather than `DateTime`. The data-types modal hosts the same component
 * with its own `primitives` (the catalog builder's `SCALAR_TYPE_OPTIONS`) and `hideManage`
 * — the two vocabularies must never be mixed, so each host supplies its own.
 */

// The tab keys double as their labels, which the E2E suite resolves the tabs by (their radio's name).
const PRIMITIVE_TAB: string = "Primitive Type";
const CATALOG_TAB: string = "Data Types";

/**
 * The "Primitive Type" tab — the single source of the workflow parameter vocabulary.
 *
 * The names have to be members of the backend's `EDataType`, which is what the workflow JSON
 * deserialises into. A name that is not one throws before code generation starts — "Error
 * converting value ... to type EDataType" — and that failure takes down every project in the
 * workspace, not just the workflow that used it.
 *
 * So `Bytea`, `Byte` and `Single` are NOT here: `EDataType` has no such member. They were
 * offered by mistake and reached production. `Image` IS a member but stays out too — it is not
 * offered as a workflow parameter type.
 */
export const DATA_TYPE_PRIMITIVES: string[] = [
  "Boolean",
  "DateTimeOffset",
  "Decimal",
  "Double",
  "Guid",
  "Int32",
  "Int64",
  "String"
];

/**
 * Icon per primitive so the list reads like the design. Every name is an asset that
 * exists in `public/images/common` — KuikaSvg masks a missing file to nothing, which
 * fails silently, so this map is deliberately conservative.
 */
const PRIMITIVE_ICON_FALLBACK: string = "code";
const PRIMITIVE_ICONS: Record<string, string> = {
  Boolean: "circle-half-vertical",
  DateTimeOffset: "calendar-event",
  Decimal: "number-8",
  Double: "number-8",
  Guid: "key",
  Int32: "number-8",
  Int64: "number-8",
  String: "text-size"
};

/** "DateTimeOffset" reads as "Date Time Offset" in the dropdown. */
export const humanizeDataType = (s: string): string => s.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

/**
 * The icon a data type reads with, shared by the option rows and any combobox trigger
 * hosting this dropdown — so there is one icon map. Catalog types are always "components";
 * an empty value reads as "edit" (the "choose a type" affordance).
 */
export const getDataTypeIcon = (name: string | undefined, isCatalogType: boolean): string => {
  if (!name) return "edit";
  if (isCatalogType) return "components";
  return PRIMITIVE_ICONS[name] ?? PRIMITIVE_ICON_FALLBACK;
};

/** A "Primitive Type" tab entry. Hosts with a different vocabulary (the data-types modal's
 *  catalog scalars) pass their own list; the default is the workflow parameter primitives. */
export interface IDataTypePrimitiveOption {
  value: string;
  label: string;
  icon: string;
}

const DEFAULT_PRIMITIVES: IDataTypePrimitiveOption[] = DATA_TYPE_PRIMITIVES.map(
  (name: string): IDataTypePrimitiveOption => ({
    value: name,
    label: humanizeDataType(name),
    icon: getDataTypeIcon(name, false)
  })
);

export interface KuikaDataTypeDropdownProps {
  /** Current primitive value, or a catalog type name. */
  value?: string;
  /**
   * `isCatalogType` tells the caller which vocabulary the pick came from: a workflow
   * catalog entry is stored as `isObject` + `dataTypeName`, a primitive as `dataType`.
   * Hosts that store a single type string (the data-types modal) can ignore it.
   */
  onChange: (dataType: string, isCatalogType: boolean) => void;
  /** Catalog entries; only user-authored ones are offered. Ignored when `catalogNames` is given. */
  types?: IKType[];
  /**
   * Explicit catalog names — overrides `types`. The data-types modal passes this so it can
   * exclude the type currently being edited (no self-reference).
   */
  catalogNames?: string[];
  /** "Primitive Type" vocabulary; defaults to the workflow parameter primitives. */
  primitives?: IDataTypePrimitiveOption[];
  /**
   * Fired when the user clicks "Manage Data Types". The dropdown does NOT own the
   * catalog modal: it lives inside the host's combobox popover, and opening the modal
   * closes that popover — which would unmount the modal with it. The host (which is
   * stable) opens the modal instead, in response to this event.
   */
  onManageDataTypes?: () => void;
  /** Hide the "Manage Data Types" footer — e.g. when the dropdown is hosted inside that very modal. */
  hideManage?: boolean;
  /** True while the catalog is (re)loading; the Data Types tab shows a spinner instead of a stale list. */
  loading?: boolean;
  /** Called after a selection (or when opening the manager) so the host popover can close. */
  onRequestClose?: () => void;
}

export const KuikaDataTypeDropdown = ({
  value,
  onChange,
  types,
  catalogNames: catalogNamesProp,
  primitives = DEFAULT_PRIMITIVES,
  onManageDataTypes,
  hideManage,
  loading,
  onRequestClose
}: KuikaDataTypeDropdownProps): JSX.Element => {
  const catalogNames: string[] = useMemo((): string[] => {
    if (catalogNamesProp) return catalogNamesProp;
    return (types ?? [])
      .filter((t: IKType): boolean => t.origin === "user")
      .map((t: IKType): string => t.name)
      .filter((name: string): boolean => !!name);
  }, [catalogNamesProp, types]);

  const categories: IDataTypePanelCategory[] = useMemo(
    (): IDataTypePanelCategory[] => [
      {
        key: PRIMITIVE_TAB,
        label: PRIMITIVE_TAB,
        testId: "dataTypeDropdownPrimitiveTab",
        options: primitives.map((primitive: IDataTypePrimitiveOption): IDataTypePanelOption => ({
          value: primitive.value,
          label: primitive.label,
          icon: <KuikaSvg name={primitive.icon} width={14} height={14} color="var(--dark-6)" />,
          testId: `dataTypeDropdownOption-${primitive.value}`
        }))
      },
      {
        key: CATALOG_TAB,
        label: CATALOG_TAB,
        testId: "dataTypeDropdownCatalogTab",
        loading,
        options: catalogNames.map((name: string): IDataTypePanelOption => ({
          value: name,
          label: name,
          icon: <KuikaSvg name="components" width={14} height={14} color="var(--dark-6)" />,
          testId: `dataTypeDropdownOption-${name}`
        }))
      }
    ],
    [primitives, catalogNames, loading]
  );

  const openManager = (): void => {
    onRequestClose?.();
    onManageDataTypes?.();
  };

  return (
    <KuikaDataTypePanel
      categories={categories}
      value={value}
      // Always opens on the primitives, as it always has — the catalog is one click away.
      defaultCategory={PRIMITIVE_TAB}
      onSelect={(dataType: string, categoryKey: string): void => onChange(dataType, categoryKey === CATALOG_TAB)}
      onRequestClose={onRequestClose}
      testId="dataTypeDropdown"
      searchTestId="dataTypeDropdownSearchInput"
      footer={
        !hideManage && (
          <button
            type="button"
            className="kuika-data-type-dropdown__manage"
            disabled={loading}
            onClick={openManager}
            data-testid="dataTypeDropdownManageButton"
          >
            {loading ? (
              <KuikaSmallSpinner spinning />
            ) : (
              <KuikaSvg name="settings" width={14} height={14} color="var(--blue-5)" />
            )}
            Manage Data Types
          </button>
        )
      }
    />
  );
};

export default KuikaDataTypeDropdown;
